import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
// Import de `/react`, no de `/next`: esto es una SPA con Vite. El paquete se
// sirve desde el propio dominio (/_vercel/insights), así que la CSP de
// vercel.json lo permite sin abrir ningún host nuevo.
import { Analytics } from '@vercel/analytics/react';
import { useStockStore } from './lib/stock';
import Header from './components/Header';
import Footer from './components/Footer';
import AgeGate from './components/AgeGate';
import CookieBanner from './components/CookieBanner';
import HealthWarningBanner from './components/HealthWarningBanner';
import ScrollToTop from './components/ScrollToTop';
import BackgroundFx from './components/BackgroundFx';
import WhatsAppFab from './components/WhatsApp';
import RecentPurchaseToast from './components/RecentPurchaseToast';
import Home from './pages/Home';

// ============================================================================
// Code-splitting
// ----------------------------------------------------------------------------
// `Home` se importa eager (es la landing). El resto se carga bajo demanda
// con React.lazy → bundle inicial mucho más pequeño, mejor rendimiento móvil.
// ============================================================================
const Shop         = lazy(() => import('./pages/Shop'));
const Product      = lazy(() => import('./pages/Product'));
const Checkout     = lazy(() => import('./pages/Checkout'));
const Contact      = lazy(() => import('./pages/Contact'));
const Panel        = lazy(() => import('./pages/Panel'));
const NotFound     = lazy(() => import('./pages/NotFound'));
const AvisoLegal   = lazy(() => import('./pages/legal/AvisoLegal'));
const Privacidad   = lazy(() => import('./pages/legal/Privacidad'));
const Cookies      = lazy(() => import('./pages/legal/Cookies'));
const Terminos     = lazy(() => import('./pages/legal/Terminos'));
const Devoluciones = lazy(() => import('./pages/legal/Devoluciones'));

// Fallback mínimo mientras carga el chunk de una ruta.
function RouteLoader() {
  return (
    <div className="container-app py-32 text-center text-bone-400 text-sm">
      Cargando…
    </div>
  );
}

export default function App() {
  // `useLocation` → cambia con cada navegación. La usamos como `key` en el
  // div que envuelve las rutas para que React lo remonte y dispare la
  // animación `page-in` (definida en tailwind.config.js).
  //
  // `motion-safe:` hace que la animación se desactive automáticamente
  // cuando el usuario tiene activada la preferencia "reducir movimiento".
  const location = useLocation();

  // Disponibilidad de todo el catálogo en una sola petición al arrancar.
  // El store ignora las llamadas repetidas, así que no se pide más de una vez.
  const loadStock = useStockStore((s) => s.load);
  useEffect(() => {
    loadStock();
  }, [loadStock]);

  return (
    <div className="relative isolate flex min-h-screen flex-col">
      <BackgroundFx />
      <ScrollToTop />
      <Header />
      <main className="relative z-10 flex-1">
        <Suspense fallback={<RouteLoader />}>
          <div key={location.pathname} className="animate-page-in">
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/tienda" element={<Shop />} />
              <Route path="/tienda/marca/:marca" element={<Shop />} />
              <Route path="/tienda/:categoria" element={<Shop />} />
              <Route path="/tienda/:categoria/marca/:marca" element={<Shop />} />
              <Route path="/producto/:slug" element={<Product />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/contacto" element={<Contact />} />
              {/* Uso interno: no se enlaza desde ninguna parte de la tienda ni entra
                  en el sitemap, y la página se marca como noindex. */}
              <Route path="/panel" element={<Panel />} />
              <Route path="/aviso-legal" element={<AvisoLegal />} />
              <Route path="/politica-privacidad" element={<Privacidad />} />
              <Route path="/politica-cookies" element={<Cookies />} />
              <Route path="/terminos-condiciones" element={<Terminos />} />
              <Route path="/politica-devoluciones" element={<Devoluciones />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </Suspense>
      </main>
      <Footer />
      {/* La advertencia sanitaria cierra la página, debajo del pie. */}
      <HealthWarningBanner />
      {/* El panel es de uso interno: ahí el botón de atención al cliente sobra. */}
      {location.pathname !== '/panel' && <WhatsAppFab />}
      <RecentPurchaseToast />
      {/* Analítica de Vercel: cuenta visitas y páginas vistas sin cookies ni
          identificadores persistentes, así que no depende del aviso de cookies. */}
      <Analytics />
      <AgeGate />
      <CookieBanner />
    </div>
  );
}
